import { SearchOutlined } from '@ant-design/icons';
import { useInterval, useMount } from 'ahooks';
import { Badge, Input, Layout } from 'antd';
import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReactComponent as MainLogo } from 'assets/img/main-logo.svg';
import classNames from 'classnames';
import { NotificationIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { JobConfigSourceEnum } from 'domains/jobconfigDomain';
import { usePage } from 'hooks';
import { useCanvaProductSwitch } from 'hooks/useCanvaProductSwitch';
import { useSsoBuyerExperience } from 'hooks/useSsoBuyerExperience';
import { useOrg } from 'hooks/useOrg';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import appService from 'services/appService';
import { isBuyer, isOrgAdmin, isSourcer, isSupplier } from 'utils/rbac';
import { Cart } from './Cart';
import './Header.less';
import OrderHistory from './OrderHistory';
import Profile from './Profile';
import { Report } from './Report';
import Shop from './Shop';
import SsoBuyerShopDropdown from './SsoBuyerShopDropdown';
import { SiteRoleEnum } from 'domains/roleDomain';

const CANVA_BRAND_TEMPLATES_URL =
  'https://www.canva.com/brand/brand-templates';

const Header = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // const configItem = useAppSelector((state) => state.jobConfig.configItem);
  const project = useAppSelector((state) => state.project);
  const jobconfigSource = useAppSelector(
    (state) => state.jobConfig.jobconfigSource
  );
  const history = useHistory();
  const {
    isLoginPage,
    isShoppingCartPage,
    isOrderHistoryPage,
    isWorkspacePage,
    isNotificationPage,
    isJobConfigPage,
    isJobArchivePage,
    isProjectPage,
    isSsoBuyerCompanyGalleryPage,
  } = usePage();
  const cartItems = useAppSelector((state) => state.cart.cartNowIds);
  const cartTotal = useAppSelector((state) => state.cart.cartTotal);
  const user = useAppSelector((state) => state.app.user);
  const alertsInfo = useAppSelector((state) => state.alert.alertsInfo);
  const org = useOrg();
  const isCanvaSsoBuyer =
    isBuyer(user) && Boolean(user?.isSsoUser) && Boolean(user?.canvaUserId);
  // Not the raw account type: an SSO buyer whose org has no SSO-buyer gallery, or who came from
  // Canva, gets the ordinary header -- its Shop menu reads the labels they actually have, while
  // the SSO-buyer one reads a catalogue that is empty for them. See useSsoBuyerExperience.
  const isSsoBuyerExperience = useSsoBuyerExperience();
  // A Canva buyer who picked this product out of the suggestions can pick another one, with the
  // design carried over. See useCanvaProductSwitch.
  const { canSwitchProduct, switchProduct } = useCanvaProductSwitch();
  const goToCanvaBrandTemplates = () => {
    window.open(CANVA_BRAND_TEMPLATES_URL, '_blank', 'noopener,noreferrer');
  };
  console.log('Header render org: ', org);
  const logo =
    org?.logo ||
    user?.manageOrgs?.find((item) => item.id === user?.orgId)?.logo;
  const mainLogo = logo ? (
    <img
      src={`${process.env.REACT_APP_UPLOAD_URL_PREFIX}${logo}`}
      alt="main-logo"
    />
  ) : (
    <MainLogo />
  );

  useMount(() => {
    if (user) {
      dispatch.cart.getList();
      if (!isBuyer(user)) {
        dispatch.alert.getNotificationAndAlerts();
      }
    }
  });

  useInterval(() => {
    if (user && !isBuyer(user) && !isJobConfigPage) {
      // dispatch.alert.getNotificationAndAlerts();
    }
  }, 10000);

  useInterval(() => {
    if (user) {
      const userFromToken = appService.getUserFromJwtToken();
      if (user.userId !== userFromToken?.userId) {
        window.location.pathname = '/';
      }
    }
  }, 10000);

  return (
    <div
      className={`header-container ${
        isJobConfigPage ? 'header-container-config' : ''
      }`}
    >
      <Layout.Header className="app-header">
        <div className="app-header-left">
          <Link to="/" className="app-header-left-logo">
            {mainLogo}
          </Link>
          {!isLoginPage && (
            <Input
              className="app-header-left-search"
              placeholder={t('header.search')}
              suffix={<SearchOutlined />}
            />
          )}
        </div>
        {!isLoginPage && (
          <div className="app-header-right">
            {isJobConfigPage ? (
              <>
                {Boolean(project?.jobProjectId) && (
                  <div className="no-style">
                    <RevxButton
                      brandGreen
                      type="primary"
                      onClick={() => {
                        history.push(`/projects/${project.jobProjectId}`);
                      }}
                    >
                      <div>{t('header.returnToProject')}</div>
                    </RevxButton>
                  </div>
                )}
                {/* Its own button rather than a branch of the one below: a Canva buyer needs
                    both -- this one changes the product the design goes on, "switch Canva
                    templates" changes the design itself. */}
                {canSwitchProduct && (
                  <div className="no-style">
                    <RevxButton brandGreen type="ghost" onClick={switchProduct}>
                      <div>{t('header.chooseDifferentProduct')}</div>
                    </RevxButton>
                  </div>
                )}
                <div className="no-style">
                  {isCanvaSsoBuyer ? (
                    <RevxButton
                      brandGreen
                      type="ghost"
                      onClick={goToCanvaBrandTemplates}
                    >
                      <div>SWITCH CANVA TEMPLATES</div>
                    </RevxButton>
                  ) : (
                    <>
                      {!canSwitchProduct &&
                        jobconfigSource === JobConfigSourceEnum.SHOP &&
                        (isSsoBuyerExperience ? (
                          <SsoBuyerShopDropdown>
                            <RevxButton brandGreen type="ghost">
                              <div>{t('header.chooseDifferentProduct')}</div>
                            </RevxButton>
                          </SsoBuyerShopDropdown>
                        ) : (
                          <Shop>
                            <RevxButton brandGreen type="ghost">
                              <div>{t('header.chooseDifferentProduct')}</div>
                            </RevxButton>
                          </Shop>
                        ))}
                      {!canSwitchProduct &&
                        jobconfigSource === JobConfigSourceEnum.GALLERY && (
                          <RevxButton
                            brandGreen
                            type="ghost"
                            onClick={() => {
                              history.push(routesMap.gallery.path);
                            }}
                          >
                            <div>{t('header.chooseDifferentProduct')}</div>
                          </RevxButton>
                        )}
                    </>
                  )}
                </div>
                {
                  <div className="no-style">
                    <RevxButton
                      brandGreen
                      type="ghost"
                      onClick={() => {
                        history.push(routesMap.workspace.path);
                      }}
                    >
                      <div>{t('header.goToWorkspace')}</div>
                    </RevxButton>
                  </div>
                }
              </>
            ) : isSsoBuyerExperience ? (
              <>
                {isSsoBuyerCompanyGalleryPage && (
                  <>
                    <RevxButton
                      style={{
                        marginRight: '16px',
                        color: '#52B3A7',
                        borderColor: '#52B3A7',
                        fontWeight: 500,
                      }}
                      type="default"
                      onClick={
                        isCanvaSsoBuyer
                          ? goToCanvaBrandTemplates
                          : () => history.goBack()
                      }
                    >
                      {isCanvaSsoBuyer
                        ? 'SWITCH CANVA TEMPLATES'
                        : 'CHOOSE DIFFERENT PRODUCT'}
                    </RevxButton>
                    <RevxButton
                      style={{
                        marginRight: '16px',
                        color: '#52B3A7',
                        borderColor: '#52B3A7',
                        fontWeight: 500,
                      }}
                      type="default"
                      onClick={() => {
                        history.push(routesMap.workspace.path);
                      }}
                    >
                      <div>{t('header.goToWorkspace')}</div>
                    </RevxButton>
                  </>
                )}
                {!isSsoBuyerCompanyGalleryPage && (
                  <>
                    <Link
                      to={routesMap.workspace.path}
                      className={classNames({
                        active: isWorkspacePage,
                      })}
                    >
                      <div>{t('header.workspace')}</div>
                    </Link>

                    {!org?.hideProject && (
                      <Link
                        to={routesMap.projects.path}
                        className={classNames({
                          active: isProjectPage,
                        })}
                      >
                        <div>{t('header.projects')}</div>
                      </Link>
                    )}

                    <SsoBuyerShopDropdown />
                    <Link
                      to={routesMap.orderHistory.path}
                      className={classNames({
                        active: isOrderHistoryPage,
                      })}
                    >
                      <OrderHistory />
                    </Link>
                  </>
                )}
              </>
            ) : (
              <>
                <Link
                  to={routesMap.workspace.path}
                  className={classNames({
                    active: isWorkspacePage,
                  })}
                >
                  <div>{t('header.workspace')}</div>
                </Link>


                {!org?.hideProject && (
                  <Link
                    to={routesMap.projects.path}
                    className={classNames({
                      active: isProjectPage,
                    })}
                  >
                    <div>{t('header.projects')}</div>
                  </Link>
                )}


                {user?.siteRole === SiteRoleEnum.Supplier && (
                  <Link
                    to={routesMap.jobArchive.path}
                    className={classNames({
                      active: isJobArchivePage,
                    })}
                  >
                    <div>{t('header.jobArchive')}</div>
                  </Link>
                )}

                <Shop />

                <Link
                  to={routesMap.orderHistory.path}
                  className={classNames({
                    active: isOrderHistoryPage,
                  })}
                >
                  <OrderHistory />
                </Link>

                {user &&
                  (isSourcer(user) || isOrgAdmin(user) || isSupplier(user)) && (
                    <Report />
                  )}
              </>
            )}

            <Cart
              orderNowIds={cartItems}
              isShoppingCartPage={isShoppingCartPage}
              cartTotal={cartTotal}
            />

            {user && !isBuyer(user) && (
              <Link
                to={routesMap.notification.path}
                style={{ padding: '5.5px 14px 5px 16px' }}
                className={classNames({
                  active: isNotificationPage,
                })}
              >
                <Badge count={alertsInfo?.totalCount} size="small">
                  <div className="app-header-right-cart">
                    <NotificationIcon />
                  </div>
                </Badge>
              </Link>
            )}
            <Profile />
          </div>
        )}
      </Layout.Header>
    </div>
  );
};

export default Header;
